/**
 * Before/After Image Comparison Block
 * Interactive slider to compare two images.
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';
import { iconArrowLeftRight } from '../../icons';

// =============================================================================
// TYPES
// =============================================================================

interface BeforeAfterData {
  before: { image: string; label?: string };
  after: { image: string; label?: string };
  title?: string;
  initialPosition?: number;
}

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidBeforeAfterData(data: unknown): data is BeforeAfterData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Must have before and after objects with image strings
  if (!d.before || typeof d.before !== 'object') return false;
  if (!d.after || typeof d.after !== 'object') return false;

  const before = d.before as Record<string, unknown>;
  const after = d.after as Record<string, unknown>;

  if (typeof before.image !== 'string' || !before.image) return false;
  if (typeof after.image !== 'string' || !after.image) return false;

  return true;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderBeforeAfter(data: BeforeAfterData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('before-after');

  const initialPos = data.initialPosition ?? 50;

  let html = '';
  if (data.title) {
    html += `<h3 class="pms-before-after-title">${escapeHtml(data.title)}</h3>`;
  }

  html += `
    <div class="pms-before-after-container">
      <div class="pms-before-after-img pms-before-after-after">
        <img src="${escapeHtml(data.after.image)}" alt="${escapeHtml(data.after.label || 'After')}" loading="lazy" decoding="async">
        ${data.after.label ? `<span class="pms-before-after-label pms-before-after-label-after">${escapeHtml(data.after.label)}</span>` : ''}
      </div>
      <div class="pms-before-after-overlay" style="width: ${initialPos}%;">
        <img src="${escapeHtml(data.before.image)}" alt="${escapeHtml(data.before.label || 'Before')}" loading="lazy" decoding="async">
        ${data.before.label ? `<span class="pms-before-after-label pms-before-after-label-before">${escapeHtml(data.before.label)}</span>` : ''}
      </div>
      <div class="pms-before-after-slider" style="left: ${initialPos}%;">
        <span class="pms-before-after-slider-icon">${iconArrowLeftRight()}</span>
      </div>
    </div>
  `;

  html += `
    <div class="pms-before-after-labels">
      <span class="pms-before-after-label">${escapeHtml(data.before.label || 'Before')}</span>
      <span class="pms-before-after-label">${escapeHtml(data.after.label || 'After')}</span>
    </div>
  `;

  wrapper.innerHTML = html;

  // Get elements
  const baContainer = wrapper.querySelector('.pms-before-after-container') as HTMLElement;
  const overlay = wrapper.querySelector('.pms-before-after-overlay') as HTMLElement;
  const slider = wrapper.querySelector('.pms-before-after-slider') as HTMLElement;

  let isDragging = false;

  function updatePosition(clientX: number): void {
    const rect = baContainer.getBoundingClientRect();
    let percent = ((clientX - rect.left) / rect.width) * 100;
    percent = Math.max(0, Math.min(100, percent));

    slider.style.left = `${percent}%`;
    overlay.style.width = `${percent}%`;
  }

  // Mouse events
  slider.addEventListener('mousedown', (e) => {
    isDragging = true;
    e.preventDefault();
  });

  document.addEventListener('mouseup', () => {
    isDragging = false;
  });

  document.addEventListener('mousemove', (e) => {
    if (isDragging) {
      updatePosition(e.clientX);
    }
  });

  // Touch events
  slider.addEventListener('touchstart', () => {
    isDragging = true;
  });

  document.addEventListener('touchend', () => {
    isDragging = false;
  });

  baContainer.addEventListener('touchmove', (e) => {
    if (isDragging && e.touches[0]) {
      e.preventDefault();
      updatePosition(e.touches[0].clientX);
    }
  });

  // Click to position
  baContainer.addEventListener('click', (e) => {
    if (!isDragging) {
      updatePosition(e.clientX);
    }
  });

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('before-after', (data: unknown, container: HTMLElement) => {
  if (!isValidBeforeAfterData(data)) {
    console.warn('[before-after] Invalid data:', data);
    const wrapper = createBlockWrapper('before-after');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid before/after data - requires before.image and after.image</div>';
    container.appendChild(wrapper);
    return;
  }

  renderBeforeAfter(data, container);
});
